/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import React from 'react';
import {
  Dialog, DialogClose, DialogContent, DialogDescription,
  DialogFooter, DialogHeader, DialogTitle,
} from '@/shared/ui';
import Button from '@/shared/ui/components/basic/Button';

export interface SpreadsheetConfirmDialogProps {
  open: boolean;
  title: string;
  description: string;
  confirmLabel: string;
  cancelLabel: string;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
}

/**
 * Confirmation for the one destructive action this area has: undoing an
 * import.
 *
 * Local rather than shared because there is no shared confirm dialog yet --
 * launches, agency-site-config and the publications centre each carry their
 * own copy. Promoting one of them into `shared/ui` would touch three features
 * this change has no business editing; the duplication is flagged instead.
 *
 * @param props - Copy, open state, and the confirm handler.
 * @returns The rendered dialog.
 */
export const SpreadsheetConfirmDialog: React.FC<SpreadsheetConfirmDialogProps> = ({
  open, title, description, confirmLabel, cancelLabel, onOpenChange, onConfirm,
}) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent data-testid="spreadsheet-confirm-dialog">
      <DialogHeader>
        <DialogTitle>{title}</DialogTitle>
        <DialogDescription>{description}</DialogDescription>
      </DialogHeader>
      <DialogFooter>
        <DialogClose asChild>
          <Button data-testid="spreadsheet-confirm-cancel" variant="outline">
            {cancelLabel}
          </Button>
        </DialogClose>
        <Button
          data-testid="spreadsheet-confirm-accept"
          onClick={onConfirm}
          className="bg-(--status-error-text) hover:bg-(--status-error-text)/90 text-(--text-inverse)"
        >
          {confirmLabel}
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);
